import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
  TAction,
  IOrdersGetData,
  TPaginationResponse,
  TGetOneEntityAction,
  TNoteCreateAction,
  TVisitsResponse,
  TProductVariationActionObject,
  TFilterOptions,
  TGetPharmacyUpdate,
  TSendOrderLink
} from 'shared/types';
import { IOrder } from 'shared/interfaces/order.interface';
import { IPaymentMethod } from 'shared/interfaces/payment-method.interface';
import { IState } from 'shared/interfaces/state.interface';
import { INote } from 'shared/interfaces/note.interface';
import OrderService from 'shared/services/OrderService';
import BillingsService from 'shared/services/BillingsService';
import NoteService from 'shared/services/NoteService';
import VisitsService from 'shared/services/VisitsService';
import PrescriptionFulfillmentService from 'shared/services/PrescriptionFulfillmentService';
import {
  getOrdersData,
  setOrdersData,
  getOrdersFail,
  setOrdersReportData,
  getOrder as getOrderAction,
  setOrder,
  removeNote,
  addNewNote,
  createOrderNote,
  deleteOrderNote,
  updateOrder,
  updateDataOrder,
  leaveWaitingRoom,
  updateOrderStatus,
  expediteOrder,
  expediteOrderData,
  cancelOrder,
  createJiraServiceTicket,
  updateOrderStatusAction,
  elevateVisitType,
  updateCurrentOrder,
  assignAffiliateAdmin,
  assignAffiliateAdminAction,
  submitOrderSuccess,
  submitOrder,
  submitOrderFail,
  updateDataOrderFail,
  updatePrerequisiteInstance as updatePrerequisiteInstanceAction,
  setPrerequisiteInstance,
  uploadFilePrerequisiteInstance as uploadFilePrerequisiteInstanceAction,
  setFilePrerequisiteInstance,
  sendToPharmacyRecipients,
  getPharmacyUpdate,
  getCurrentOrderSelector,
  sendOrderLink,
  approveOrder,
  approveOrderSuccess,
  approveOrderFail
} from './orders.redux';
import { openNotificationMessage } from 'shared/utils/NotificationMessages';
import { NotificationMessages, OrderStatuses } from 'shared/enums';
import { AxiosResponse } from 'axios';
import {
  IPrerequisiteInstance,
  IUpdatePrerequisiteInstance,
  IUploadFilePrerequisiteInstance
} from 'shared/interfaces/prerequisite.interface';
import PrerequisiteService from 'shared/services/PrerequisiteService';
import { IPrescriptionFulfillment } from 'shared/interfaces/prescriptionFulfillment.interface';
import {
  getCurrentVisitSelector,
  getVisitsFail,
  setVisit
} from 'store/visits/visits.redux';
import { IVisit } from 'shared/interfaces/visit.interface';

function* getOrders({ payload }: TAction<IOrdersGetData>) {
  const { isReport } = payload;
  try {
    const result: TPaginationResponse<IOrder> = yield call(
      OrderService.getAll,
      payload // Pass full payload including isReport for timeout handling
    );

    if (isReport) {
      yield put(setOrdersReportData(result));
      return;
    }
    yield put(setOrdersData(result));
  } catch (error: any) {
    console.log('error', error);

    // Show specific timeout message for reports
    if (isReport && error?.code === 'ECONNABORTED') {
      yield openNotificationMessage({
        type: NotificationMessages.Error,
        title: 'Request Timeout',
        message:
          'The report request timed out. Try a shorter date range to narrow results.'
      });
    }

    yield put(getOrdersFail());
  }
}

function* handleOrderData({
  order,
  needPaymentMethod
}: {
  order: IOrder;
  needPaymentMethod?: boolean;
}) {
  const [notes, visitResponse]: [INote[], TVisitsResponse] = yield all([
    call(NoteService.getOrderNotes, order.id),
    call(VisitsService.getVisitsByOrder, order.id)
  ]);
  let paymentMethod: IPaymentMethod | null = null;

  if (needPaymentMethod && order.paymentIntent?.paymentMethod) {
    paymentMethod = yield call(BillingsService.getPaymentMethodData, {
      id: order.paymentIntent?.paymentMethod,
      patient: order.patient.id
    });
  }

  yield put(
    setOrder({
      data: order,
      paymentMethod: paymentMethod,
      notes,
      visits: visitResponse.visits
    })
  );
}

function* getOrder({ payload }: TAction<TGetOneEntityAction>) {
  try {
    const order: IOrder = yield call(OrderService.getOneOrder, payload.id);
    yield call(handleOrderData, {
      order,
      needPaymentMethod: payload?.needPaymentMethod
    });
  } catch (error) {
    console.log('error', error);
    yield put(getOrdersFail());
  }
}

function* createNote({ payload }: TAction<TNoteCreateAction>) {
  try {
    const currentOrder: IOrder = yield select(
      (s: IState) => s.orders.currentOrder.data
    );

    const result: INote = yield call(NoteService.createNote, {
      ...payload,
      relatedEntity: currentOrder.id
    });

    yield put(addNewNote(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Note was successfully added.'
    });
  } catch (error) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: 'Note creation was unsuccesful.'
    });
  }
}

function* deleteNote({ payload }: TAction<string>) {
  try {
    yield call(NoteService.deleteNote, payload);
    yield put(removeNote(payload));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Note was successfully deleted.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data.message || 'Unable to delete note.'
    });
  }
}

function* updateOrderData({
  payload
}: TAction<{
  productVariations: TProductVariationActionObject[];
  id: string;
  newData: IOrder;
  addressAndProjectModification: boolean;
  forcedVisitType: boolean;
  data: Partial<IOrder>;
}>) {
  try {
    let order: IOrder | null = null;
    let errors: string[] = [];

    if (payload?.data) {
      order = yield call(OrderService.updateOrder, {
        id: payload.id,
        newData: payload.data
      });
      yield put(updateCurrentOrder(order));
    }

    if (payload?.addressAndProjectModification === true) {
      order = yield call(OrderService.updateOrder, {
        id: payload.id,
        newData: payload.newData
      });
    }

    if (payload?.productVariations?.length) {
      const data: { order: IOrder; errors: string[] } = yield call(
        OrderService.updateOrderProducts,
        {
          id: payload.id,
          productVariations: payload.productVariations
        }
      );
      order = data.order;
      errors = data.errors;
    }

    if (payload?.forcedVisitType) {
      order = yield call(OrderService.updateOrder, {
        id: payload.id,
        newData: { forcedVisitType: payload.forcedVisitType }
      });
      yield put(updateCurrentOrder(order));
    } else {
      const visitsData: AxiosResponse<TVisitsResponse> = yield call(
        VisitsService.getVisitsByOrder,
        payload.id
      );

      yield put(updateOrder({ order, visits: visitsData.visits }));
    }

    if (errors.length) {
      yield openNotificationMessage({
        type: NotificationMessages.Error,
        title: 'Some of the products were not updated',
        message: errors.join('; ')
      });
    } else {
      yield openNotificationMessage({
        type: NotificationMessages.Success,
        title: 'Success',
        message: 'Order was successfully updated.'
      });
    }
  } catch (error: any) {
    console.log('error', error);
    yield put(updateDataOrderFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data?.message || 'Order was not updated.'
    });
  }
}

function* updateOrderStatusSaga({
  payload
}: TAction<{ id: string; status: OrderStatuses }>) {
  try {
    const result: TPaginationResponse<IOrder> = yield call(
      OrderService.updateOrderStatus,
      payload
    );
    yield put(updateOrderStatus(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Order status was successfully updated.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message || 'Unable to update order status.'
    });
  }
}

function* leaveWaitingRoomStatusUpdated({ payload }: TAction<{ id: string }>) {
  try {
    const result: TPaginationResponse<IOrder> = yield call(
      OrderService.updateOrderStatusToLeaveWaitingRoom,
      payload
    );
    yield put(updateOrderStatus(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Waiting Room successfully left.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data?.message || 'Unable to leave waiting room.'
    });
  }
}
function* updateExpediteOrder({
  payload
}: TAction<{ id: string; expedited: boolean }>) {
  try {
    const result: TPaginationResponse<IOrder> = yield call(
      OrderService.expediteOrder,
      payload
    );
    yield put(expediteOrder(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Order was successfully expedited.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield put(getOrdersFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data?.message || 'Unable to expedite order.'
    });
  }
}
function* statusUpdatedToCancelled({ payload }: TAction<{ id: string }>) {
  try {
    const result: TPaginationResponse<IOrder> = yield call(
      OrderService.cancelOrder,
      payload
    );
    yield put(updateOrderStatus(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Order was successfully cancelled.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data?.message || 'Unable to cancel the order.'
    });
  }
}

function* createJiraServiceTicketSaga({
  payload
}: TAction<{ orderId: string; requestType: string }>) {
  try {
    const result: { serviceTicketUrl: string } = yield call(
      OrderService.createJiraServiceTicket,
      payload
    );

    window.open(result.serviceTicketUrl);
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Unable to create Jira service ticket.'
    });
  }
}

function* assignAffiliateAdminSaga({
  payload
}: TAction<{ assignAffiliateAdmin: boolean; id: string }>) {
  try {
    const order: IOrder = yield call(
      OrderService.assignAffiliateAdmin,
      payload
    );
    yield put(assignAffiliateAdminAction(order));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'The Affiliate Admin was successfully assigned to the order'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message ||
        'Unable to assign the Affiliate Admin.'
    });
  }
}

function* elevateVisitTypeSaga({ payload }: TAction<string>) {
  try {
    const result: IOrder = yield call(OrderService.elevateVisitType, payload);
    yield put(updateCurrentOrder(result));
    openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'The order visit type was successfully updated.'
    });
  } catch (error: any) {
    console.log('error', error);
    openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.error ||
        'The order visit type updating was unsuccessful.'
    });
  }
}

function* submitOrderSaga({
  payload
}: TAction<{ id: string; filterOptions: TFilterOptions }>) {
  try {
    const result: IOrder = yield call(OrderService.submitOrder, payload.id);
    yield call(handleOrderData, {
      order: result,
      needPaymentMethod: true
    });
    if (payload?.filterOptions) {
      yield call(getOrders, { payload: payload.filterOptions });
    }
    yield put(submitOrderSuccess(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Order was successfully submitted.'
    });
  } catch (error: any) {
    console.log('submitOrderSaga error', error);
    yield put(submitOrderFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data.message || 'Order submit was unsuccessful.'
    });
  }
}

function* getPharmacyUpdateSaga({ payload }: TAction<{ id: string }>) {
  try {
    const result: TGetPharmacyUpdate = yield call(
      PrescriptionFulfillmentService.getPharmacyUpdate,
      payload.id
    );

    const updatePrescriptionFulfillments = (
      prescriptionFulfillments: IPrescriptionFulfillment[]
    ) => {
      return prescriptionFulfillments.map((pf: IPrescriptionFulfillment) => {
        if (pf.id === result.prescriptionFulfillment.id) {
          return {
            ...pf,
            shippingDetails: result.prescriptionFulfillment.shippingDetails,
            history: result.history
          };
        }
        return pf;
      });
    };

    // for Order page
    const orderData: {
      order: IOrder;
      paymentMethod: IPaymentMethod;
      notes: INote[];
      visits: IVisit[];
    } = yield select(getCurrentOrderSelector);
    if (orderData.order) {
      const order = {
        ...orderData.order,
        prescriptionFulfillments: updatePrescriptionFulfillments(
          orderData.order.prescriptionFulfillments
        )
      };

      const visits = [
        ...orderData.visits.map((visit: IVisit) => {
          const prescriptionFulfillments = updatePrescriptionFulfillments(
            visit.prescription.prescriptionFulfillments
          );
          return {
            ...visit,
            prescription: {
              ...visit.prescription,
              prescriptionFulfillments
            }
          };
        })
      ];

      yield put(setOrder({ ...orderData, data: order, visits }));
    }

    // for Visit page
    const visitData: {
      visit: IVisit;
      paymentMethod: IPaymentMethod;
      notes: INote[];
    } = yield select(getCurrentVisitSelector);
    if (visitData.visit) {
      const visit = {
        ...visitData.visit,
        prescription: {
          ...visitData.visit.prescription,
          prescriptionFulfillments: updatePrescriptionFulfillments(
            visitData.visit.prescription.prescriptionFulfillments
          )
        }
      };
      yield put(setVisit({ ...visitData, data: visit }));
    }

    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Tracking Information was successfully updated.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield put(getOrdersFail()); // Order page
    yield put(getVisitsFail()); // Visit page
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: `Unable to update prescription status. ${error?.response?.data?.message}`
    });
  }
}

function* updatePrerequisiteInstance({
  payload
}: TAction<IUpdatePrerequisiteInstance>) {
  try {
    const result: AxiosResponse<IPrerequisiteInstance> = yield call(
      PrerequisiteService.updatePrerequisiteInstance,
      payload
    );
    yield put(setPrerequisiteInstance(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Prerequisite was successfully updated.'
    });
  } catch (error) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: 'Prerequisite updating was unsuccessful.'
    });
  }
}

function* uploadFilePrerequisiteInstance({
  payload
}: TAction<IUploadFilePrerequisiteInstance>) {
  try {
    const result: AxiosResponse<IPrerequisiteInstance> = yield call(
      PrerequisiteService.uploadFile,
      payload
    );
    yield put(setFilePrerequisiteInstance(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'The file was successfully uploaded.'
    });
  } catch (error) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: 'The file uploading was unsuccessful.'
    });
  }
}

function* sendToPharmacyRecipientsSaga({
  payload
}: TAction<{ orderIdentifier: string }>) {
  try {
    yield call(OrderService.sendToPharmacyRecipients, payload);

    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'The prescription was sent to Pharmacy.'
    });

    const currentOrder: IOrder = yield select(
      (state: IState) => state.orders.currentOrder.data
    );
    if (currentOrder) {
      yield put(
        getOrderAction({ id: currentOrder.id, needPaymentMethod: true })
      );
    }
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: `The prescription was not sent to Pharmacy. ${
        error?.response?.data.message || error?.response?.data.error
      }`
    });
  }
}

function* sendOrderLinkSaga({ payload }: TAction<TSendOrderLink>) {
  try {
    const result: IOrder = yield call(OrderService.sendOrderLink, payload);
    yield put(updateCurrentOrder(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'You successfully sent a visit link to the patient.'
    });
  } catch (error: any) {
    console.log('sendOrderLinkSaga error', error);
    yield put(updateDataOrderFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data.message ||
        'A visit link sending was unsuccessful.'
    });
  }
}

function* approveOrderSaga({ payload }: TAction<string>) {
  try {
    const result: IOrder = yield call(OrderService.approveOrder, payload);
    yield put(approveOrderSuccess(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'You successfully approved the order.'
    });
  } catch (error: any) {
    console.log('sendOrderLinkSaga error', error);
    yield put(approveOrderFail(null));
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data.message || 'Order approval failed.'
    });
  }
}

function* Orders() {
  yield takeLatest(getOrdersData.type, getOrders);
  yield takeLatest(getOrderAction.type, getOrder);
  yield takeLatest(createOrderNote.type, createNote);
  yield takeLatest(deleteOrderNote.type, deleteNote);
  yield takeLatest(updateDataOrder.type, updateOrderData);
  yield takeLatest(leaveWaitingRoom.type, leaveWaitingRoomStatusUpdated);
  yield takeLatest(expediteOrderData.type, updateExpediteOrder);
  yield takeLatest(cancelOrder.type, statusUpdatedToCancelled);
  yield takeLatest(createJiraServiceTicket.type, createJiraServiceTicketSaga);
  yield takeLatest(assignAffiliateAdmin.type, assignAffiliateAdminSaga);
  yield takeLatest(updateOrderStatusAction.type, updateOrderStatusSaga);
  yield takeLatest(elevateVisitType.type, elevateVisitTypeSaga);
  yield takeLatest(submitOrder.type, submitOrderSaga);
  yield takeLatest(getPharmacyUpdate.type, getPharmacyUpdateSaga);
  yield takeLatest(
    updatePrerequisiteInstanceAction.type,
    updatePrerequisiteInstance
  );
  yield takeLatest(
    uploadFilePrerequisiteInstanceAction.type,
    uploadFilePrerequisiteInstance
  );
  yield takeLatest(sendToPharmacyRecipients.type, sendToPharmacyRecipientsSaga);
  yield takeLatest(sendOrderLink.type, sendOrderLinkSaga);
  yield takeLatest(approveOrder.type, approveOrderSaga);
}

export default Orders;
